import { Injectable } from '@angular/core';

import { WidgetDataSource } from '../../../../../../../models';
import { Widget } from '../../../../../../../providers/bridge-api/dashboard.service';
import { MatchmakerHandler } from '../../../../../../../providers/semantria-api/spote.service';
import { EnableClassTelemetry } from '../../../../../../../services/otel.service';
import { ChartOptionUtils } from '../../../../../../../utils/chart-options.utils';
import { BigNumberWidgetOptions } from '../../../../widget-big-number/widget-big-number.component';
import { WidgetChartOptions } from '../../../../widget-chart/widget-chart.component';
import { ChartPropertyValidatorBuilder } from './chart-property-validator';
import {
  WidgetAiInsightsValidatorService,
  MatchmakerHandlerValidationResult,
} from './widget-ai-insights-validator.service';

@EnableClassTelemetry()
@Injectable()
export class ChartWidgetAiInsightsValidatorService extends WidgetAiInsightsValidatorService {
  protected override isMatchmakerHandlerApplicable(
    handler: MatchmakerHandler,
    widget: Widget<WidgetChartOptions>,
    dataSource: WidgetDataSource,
    dataPointCount: (handler: MatchmakerHandler) => number
  ): MatchmakerHandlerValidationResult {
    const result = super.isMatchmakerHandlerApplicable(handler, widget, dataSource, dataPointCount);
    if (result.disabled) {
      return result;
    }

    const propertyValidatorBuilder = new ChartPropertyValidatorBuilder();

    switch (widget.payload.type) {
      case 'donut':
        return propertyValidatorBuilder
          .withRating(true)
          .withRatingScoring(widget.payload.donutOptions.scoring)
          .build(this)
          .validateHandler(handler);
      case 'documentCount':
        return propertyValidatorBuilder
          .withTimeSeries(ChartOptionUtils.isOverTimeAggregator(widget.payload.aggregator))
          .build(this)
          .validateHandler(handler);
      case 'numericMetadata':
        return propertyValidatorBuilder
          .withTimeSeries(true)
          .withRatingScoring(...widget.payload.numericMetadataFields.map((f) => f.scoring))
          .build(this)
          .validateHandler(handler);
      case 'breakdown':
        return propertyValidatorBuilder
          .withRatingScoring(
            widget.payload.breakdownOptions.scoring,
            widget.payload.additionalBreakdownOptions?.scoring
          )
          .build(this)
          .validateHandler(handler);
      case 'gauge':
        return propertyValidatorBuilder
          .withRating(true)
          .withRatingScoring(widget.payload.gaugeOptions.metric)
          .build(this)
          .validateHandler(handler);
      case 'bigNumber':
        const getStrategyForAnalyticItem = (analyticItem, scoring) =>
          analyticItem === 'documents' ? 'COUNT' : scoring;

        const widgetPayload = <BigNumberWidgetOptions>widget.payload;
        const scoringStrategies = [
          getStrategyForAnalyticItem(
            widgetPayload.analyticItem,
            widgetPayload.scoringStrategy ?? 'AVERAGE'
          ),
        ];
        if (widgetPayload.analyticItemTwo) {
          scoringStrategies.push(
            getStrategyForAnalyticItem(
              widgetPayload.analyticItemTwo,
              widgetPayload.scoringStrategyTwo ?? 'AVERAGE'
            )
          );
        }
        return propertyValidatorBuilder
          .withRating(true)
          .withRatingScoring(...scoringStrategies)
          .build(this)
          .validateHandler(handler);
      case 'sentimentPolarity':
        return propertyValidatorBuilder.build(this).validateHandler(handler);
      case 'sentimentScore':
        const aggregator = widget.payload.aggregator.replace('total_', '');
        return propertyValidatorBuilder
          .withTimeSeries(ChartOptionUtils.isOverTimeAggregator(widget.payload.aggregator))
          .withRatingScoring(aggregator)
          .build(this)
          .validateHandler(handler);
      case 'scatterPlot':
        return propertyValidatorBuilder.build(this).validateHandler(handler);
      case 'sentimentVolume':
        return propertyValidatorBuilder.withTimeSeries(true).build(this).validateHandler(handler);
    }

    return {
      handler,
      disabled: true,
    };
  }
}
